import React, { useEffect, useState } from 'react';
import {
  Tabs,
  Table,
  Button,
  Space,
  Tag,
  Modal,
  Spin,
  message,
  Checkbox,
  Input,
} from 'antd';
import {
  CheckOutlined,
  UndoOutlined,
  EditOutlined,
} from '@ant-design/icons';
import axios from 'axios';

import { baseUrl_SPB } from '../BaseUrl';
import { useAuth } from '../AuthProvider';
import AddTimesheetModal from './AddTimesheetModal';
import TimesheetHoursReport from './TimesheetHoursReport';
import TimesheetPenaltyCalculationsService from './TimesheetPenalCalclationsService';

const { Search } = Input;

const TimesheetManager = () => {
    const authContext = useAuth();
  const [loading, setLoading] = useState(false);
  const [timesheets, setTimesheets] = useState([]);
  const [selectedRowKeys, setSelectedRowKeys] = useState([]);
  const [selectedItem, setSelectedItem] = useState(null);
  const [modalOpen, setModalOpen] = useState(false);
  const [searchText, setSearchText] = useState('');
  const companyId = authContext.additionalUserInfo?.companyId;
const token = authContext.token;
  const isUserManager = authContext.role === 'ROLE_MANAGER' || authContext.role === 'ROLE_CMANAGER';
    const currentUserId = authContext.additionalUserInfo?.id;
  useEffect(() => {
    console.log('Current User token:', token);
    if(token)
        fetchTimesheets();
  }, [token]);

  const fetchTimesheets = async () => {
    setLoading(true);

    try {

      const response = await axios.get(
        `${baseUrl_SPB}/api/v1/rostertimeallocation/getallusertimesheets`,
        {
          headers: {
            Authorization: `Bearer ${token}`,
          },
        }
      );

      let data = response.data || [];

      if (!isUserManager) {
        data = data.filter(
          (x) => x.staffId === currentUserId
        );
      }

      setTimesheets(data);
    } catch (err) {
      console.error(err);
      message.error('Failed to load timesheets');
    } finally {
      setLoading(false);
    }
  };

  const updateApprovalStatus = async (
    item,
    status
  ) => {
    try {
      setLoading(true);
      await TimesheetPenaltyCalculationsService(token, item.id, item.staffId);

      await axios.post(
        `${baseUrl_SPB}/api/v1/rostertimeallocation/updatetimesheetstatus`,
        [{ ...item, approvalStatus: status }],
        {
          headers: {
            Authorization: `Bearer ${token}`,
          },
        }
      );

      setTimesheets((prev) =>
        prev.map((t) =>
          t.id === item.id
            ? { ...t, approvalStatus: status }
            : t
        )
      );

      message.success('Updated');
    } catch (err) {
      console.error(err);
      message.error('Update failed');
    } finally {
      setLoading(false);
    }
  };

  const approveSelected = async () => {
    try {
      setLoading(true);

      const selectedItems = pendingTimesheets.filter(
        (x) => selectedRowKeys.includes(x.id)
      );

      await axios.post(
        `${baseUrl_SPB}/api/v1/rostertimeallocation/updatetimesheetstatus`,
        selectedItems.map((x) => ({
          ...x,
          approvalStatus: 'approved',
        })),
        {
          headers: {
            Authorization: `Bearer ${token}`,
          },
        }
      );

      setTimesheets((prev) =>
        prev.map((x) =>
          selectedRowKeys.includes(x.id)
            ? {
                ...x,
                approvalStatus: 'approved',
              }
            : x
        )
      );

      setSelectedRowKeys([]);

      message.success(
        `${selectedItems.length} approved`
      );
    } catch (err) {
      console.error(err);
      message.error('Bulk approval failed');
    } finally {
      setLoading(false);
    }
  };

  const pendingTimesheets = timesheets.filter(
    (x) =>
      !x.approvalStatus ||
      x.approvalStatus === ''
  );

  const approvedTimesheets = timesheets.filter(
    (x) =>
      x.approvalStatus?.toLowerCase() ===
      'approved'
  );

  const columns = (isPending) => [
    {
      title: 'Employee',
      dataIndex: 'staffName',
    },
    {
      title: 'Date',
      dataIndex: 'date',
    },
    {
      title: 'Time',
      render: (_, record) =>
        `${record.starttime} - ${record.endtime}`,
    },
    {
      title: 'Status',
      dataIndex: 'approvalStatus',
      render: (status) => (
        <Tag
          color={
            status === 'approved'
              ? 'green'
              : 'orange'
          }
        >
          {status || 'Pending'}
        </Tag>
      ),
    },
    {
      title: 'Actions',
      width: 200,
      render: (_, record) => (
        <Space>
          <Button
            icon={<EditOutlined />}
            size="small"
            onClick={() => {
              setSelectedItem(record);
              setModalOpen(true);
            }}
          >
            Edit
          </Button>

          {isUserManager &&
            (isPending ? (
              <Button
                type="primary"
                icon={<CheckOutlined />}
                size="small"
                onClick={() =>
                  updateApprovalStatus(
                    record,
                    'approved'
                  )
                }
              >
                Approve
              </Button>
            ) : (
              <Button
                danger
                icon={<UndoOutlined />}
                size="small"
                onClick={() =>
                  updateApprovalStatus(
                    record,
                    ''
                  )
                }
              >
                Undo
              </Button>
            ))}
        </Space>
      ),
    },
  ];

  const filteredPending =
    pendingTimesheets.filter((x) =>
      JSON.stringify(x)
        .toLowerCase()
        .includes(searchText.toLowerCase())
    );

  const filteredApproved =
    approvedTimesheets.filter((x) =>
      JSON.stringify(x)
        .toLowerCase()
        .includes(searchText.toLowerCase())
    );

  return (
    <Spin spinning={loading}>
      <Tabs
        defaultActiveKey="pending"
        items={[
          {
            key: 'pending',
            label: 'Pending',
            children: (
              <>
                <Space
                  style={{
                    marginBottom: 16,
                  }}
                >
                  <Button
                    onClick={() =>
                      setSelectedRowKeys(
                        filteredPending.map(
                          (x) => x.id
                        )
                      )
                    }
                  >
                    Select All
                  </Button>

                  <Button
                    onClick={() =>
                      setSelectedRowKeys([])
                    }
                  >
                    Clear
                  </Button>

                  {selectedRowKeys.length >
                    0 &&
                    isUserManager && (
                      <Button
                        type="primary"
                        onClick={
                          approveSelected
                        }
                      >
                        Approve Selected
                      </Button>
                    )}
                </Space>

                <Table
                  rowKey="id"
                  dataSource={
                    filteredPending
                  }
                  columns={columns(true)}
                  rowSelection={{
                    selectedRowKeys,
                    onChange:
                      setSelectedRowKeys,
                  }}
                />
              </>
            ),
          },

          {
            key: 'approved',
            label: 'Approved',
            children: (
              <Table
                rowKey="id"
                dataSource={
                  filteredApproved
                }
                columns={columns(false)}
              />
            ),
          },

          {
  key: 'reports',
  label: 'Hours Report',
  children: (
    <TimesheetHoursReport
      timesheets={timesheets}
    />
  )
}
        ]}
      />

      <AddTimesheetModal
    open={modalOpen}
    onClose={() => {
        setModalOpen(false);
        setSelectedItem(null);
    }}
    onSave={() => {
        fetchTimesheets();
        setModalOpen(false);
    }}
    token={token}
    resource={{
        id: selectedItem?.staffId
    }}
    selectedItem={selectedItem}
    isUserManager={isUserManager}
    companyId={companyId}
/>
    </Spin>
  );
};

export default TimesheetManager;